"use client";

import {
  Beaker,
  CalendarClock,
  Droplets,
  FlaskConical,
  Pencil,
  Pill,
  RotateCcw,
  Sparkles,
  Trash2,
  UserRound,
} from "lucide-react";
import { useState, type DragEvent } from "react";
import type { ParsedOrder } from "@/src/services/mockAiService";
import { useLang } from "@/src/i18n";

type OrderChipProps = {
  order: ParsedOrder;
  onChange: (id: string, patch: Partial<ParsedOrder>) => void;
  onDelete: (id: string) => void;
  onDragStart?: (event: DragEvent<HTMLDivElement>, id: string) => void;
};

const icons = {
  medication: Pill,
  lab: FlaskConical,
  procedure: Beaker,
  fluid: Droplets,
  other: Pencil,
};

export function OrderChip({ order, onChange, onDelete, onDragStart }: OrderChipProps) {
  const { t } = useLang();
  const [editing, setEditing] = useState<"label" | "details" | null>(null);
  const Icon = icons[order.category];

  const draggable = Boolean(onDragStart) && editing === null;

  return (
    <div
      className={`order-chip ${order.conflict ? "has-conflict" : ""} ${order.aiGenerated ? "ai-authored" : ""} ${draggable ? "is-draggable" : ""}`}
      data-testid="order-chip"
      draggable={draggable}
      onDragStart={(event) => onDragStart?.(event, order.id)}
    >
      {order.aiGenerated && <span className="order-ai-badge"><Sparkles size={11} /> AI</span>}
      <div className="order-chip-icon"><Icon size={15} /></div>
      <div className="order-chip-copy">
        {editing === "label" ? (
          <input
            autoFocus
            aria-label="Edit order name"
            value={order.label}
            onChange={(event) => onChange(order.id, { label: event.target.value })}
            onBlur={() => setEditing(null)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === "Escape") setEditing(null);
            }}
          />
        ) : (
          <button type="button" className="editable-order-name" onClick={() => setEditing("label")}>
            <strong>{order.label}</strong><Pencil size={12} />
          </button>
        )}
        {editing === "details" ? (
          <input
            autoFocus
            aria-label={`Edit usage for ${order.label}`}
            value={order.details}
            onChange={(event) => onChange(order.id, { details: event.target.value })}
            onBlur={() => setEditing(null)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === "Escape") setEditing(null);
            }}
          />
        ) : (
          <button type="button" className="order-usage" onClick={() => setEditing("details")}>
            <span>{t("order.usage")}</span><strong>{order.details}</strong><Pencil size={11} />
          </button>
        )}
        <div className="order-chip-meta">
          <span><UserRound size={12} /> {order.orderedBy ?? "Dr. Heartbeat"}</span>
          <span><CalendarClock size={12} /> {order.orderedAt ?? "Just now"}</span>
          {order.tmtId && <span className="order-tmt">TMT {order.tmtId}</span>}
        </div>
        {order.conflict && <em>{order.conflict}</em>}
      </div>
      <div className="order-chip-actions">
        <button
          type="button"
          className={order.stat ? "is-stat" : ""}
          onClick={() => onChange(order.id, { stat: !order.stat })}
          aria-label={`Toggle STAT for ${order.label}`}
          title="STAT flag"
        >
          STAT
        </button>
        <button
          type="button"
          onClick={() =>
            onChange(order.id, {
              lane: order.lane === "one-day" ? "continuous" : "one-day",
            })
          }
          aria-label={`Move ${order.label} to other order lane`}
          title="Move to other lane"
        >
          <RotateCcw size={13} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(order.id)}
          aria-label={`Delete ${order.label}`}
          title="Delete order"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}
